import {messages} from '@smartmixin/i18n';
import type {MessageDescriptor} from 'react-intl';
import {DataGranularity} from '../../../api/types';
import * as Units from '../../Units';
import {DateTimeConfig} from '../../Dates';
import {
  createXAxisConfig,
  createYAxisConfig,
  createTooltipConfig,
  createGridConfig,
  createLegendConfig,
} from '../builders';
import {ChartTheme} from '../theme';
import {ChartConfig, ChartDataPoint, SeriesConfig} from '../types';

export interface VisibilityChartOptions {
  data: ChartDataPoint[];
  theme: ChartTheme;
  timezone: string;
  units: Units.Units;
  granularity: DataGranularity;
  height?: number;
  comparisonData?: ChartDataPoint[];
  comparisonLabel?: string;
  dateTimeConfig?: DateTimeConfig;
  /**
   * Names the series in the user's language. Required: the names are
   * messages, and English would otherwise reach every locale.
   */
  formatMessage: (descriptor: MessageDescriptor) => string;
}

export const visibilityChart = (
  options: VisibilityChartOptions,
): ChartConfig => {
  const {
    data,
    theme,
    timezone,
    units,
    granularity,
    height = 250,
    comparisonData,
    comparisonLabel,
    dateTimeConfig,
    formatMessage,
  } = options;

  const hasRange = ['3H', '6H', 'D', 'W', 'M'].includes(granularity);

  const series: SeriesConfig[] = [
    {
      id: 'visibility',
      type: 'line',
      dataKey: 'VIS',
      name: formatMessage(messages.metrics.visibility),
      color: theme.colors.primary,
      yAxisIndex: 0,
      interpolation: 'monotone',
      lineWidth: 2,
      showMarkers: false,
      zIndex: 2,
    },
  ];

  if (hasRange) {
    series.push({
      id: 'visibility-range',
      type: 'areaRange',
      dataKey: 'VIS_RANGE',
      rangeKey: 'VIS_RANGE',
      name: formatMessage(messages.charts.minMaxRange),
      color: theme.colors.primary,
      yAxisIndex: 0,
      interpolation: 'monotone',
      fillOpacity: 0.2,
      lineWidth: 0,
      showMarkers: false,
      zIndex: 0,
    });
  }

  if (comparisonData) {
    series.push({
      id: 'visibility-comparison',
      type: 'line',
      dataKey: 'VIS_COMP',
      name: comparisonLabel ?? formatMessage(messages.charts.comparison),
      color: theme.colors.textSecondary,
      yAxisIndex: 0,
      interpolation: 'monotone',
      lineWidth: 2,
      dashArray: '5 3',
      showMarkers: false,
      zIndex: 1,
    });
  }

  return {
    height,
    data,
    series,
    xAxis: createXAxisConfig({type: 'datetime', timezone, granularity, theme}),
    yAxes: [
      createYAxisConfig({
        id: 'visibility',
        metric: 'VIS',
        units,
        theme,
        domain: [0, 'auto'],
        unitLabel: Units.getUnitLabel(units, 'VIS'),
        precision: 0,
      }),
    ],
    tooltip: createTooltipConfig({
      timezone,
      granularity,
      metric: 'VIS',
      units,
      theme,
      dateTimeConfig,
    }),
    legend: createLegendConfig(series.length > 1, theme),
    grid: createGridConfig(theme),
    animate: true,
  };
};
